import React, { Component } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import {
    ElementQuery,
    Typography,
} from 'wanderu-components';

import ColumnChart from '../ColumnChart/ColumnChart';
import HorizontalAxis from '../HorizontalAxis/HorizontalAxis';
import LineChart from '../LineChart/LineChart';
import Tooltip from '../Tooltip/Tooltip';
import VerticalAxis from '../VerticalAxis/VerticalAxis';
import { calculateChartData } from '../utils';

import styles from './Chart.scss';

const propTypes = {
    className: PropTypes.string,
    chartType: PropTypes.oneOf(['bar', 'curve', 'line']),
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    dataType: PropTypes.oneOf(['number', 'price']),
    headerClassName: PropTypes.string,
    height: PropTypes.number,
    labelType: PropTypes.oneOf(['date', 'number']),
    numVerticalTicks: PropTypes.number,
    roundingFactor: PropTypes.number,
    title: PropTypes.string,
    width: PropTypes.number,
    xTickValues: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
        ]),
    ).isRequired,
};
const defaultProps = {
    className: '',
    chartType: 'bar',
    data: [],
    dataType: 'price',
    headerClassName: '',
    height: 165,
    labelType: 'date',
    numVerticalTicks: 5,
    roundingFactor: 5,
    title: '',
    width: 475,
    xTickValues: {},
};

class Chart extends Component {
    constructor(props) {
        super(props);

        this.state = {
            containerWidth: props.width,
            showTooltip: false,
            tooltip: {},
        };
    }

    _toggleTooltip = (showTooltip, tooltip) => {
        this.setState(() => ({
            showTooltip,
            tooltip,
        }));
    }

    _onQueryChange = (queryResults) => {
        this.setState(() => ({
            containerWidth: queryResults.width,
        }));
    };

    render() {
        const {
            className,
            chartType,
            data,
            dataType,
            headerClassName,
            height: containerHeight,
            labelType,
            numVerticalTicks,
            roundingFactor,
            title,
            xTickValues,
        } = this.props;
        const {
            containerWidth,
            showTooltip,
            tooltip,
        } = this.state;

        if (!data.length) {
            return null;
        }

        const chartOpts = {
            data,
            containerWidth,
            containerHeight,
            numVerticalTicks,
            roundingFactor,
        };

        const chartData = calculateChartData(chartOpts);

        const {
            columnWidth,
            dataPoints,
            scalingFactorY,
            verticalAxisLabels,
        } = chartData;

        const isLineGraph = chartType === 'curve' || chartType === 'line';

        return (
            <div className={classNames(className, styles.container)}>
                {!!title && (
                    <Typography
                        className={headerClassName}
                        component="h3"
                        variant="subtitle"
                    >
                        {title}
                    </Typography>
                )}
                <div className={styles.chartGroup}>
                    <ElementQuery
                        onChange={this._onQueryChange}
                        passAsProps={false}
                        dimensionsOnly
                    />
                    <svg className={styles.chart}>
                        <VerticalAxis
                            height={containerHeight}
                            scalingFactor={scalingFactorY}
                            type={dataType}
                            values={verticalAxisLabels}
                            width={containerWidth}
                        />
                        <HorizontalAxis
                            columnWidth={!isLineGraph
                                ? columnWidth
                                : 0}
                            data={dataPoints}
                            height={containerHeight}
                            isLineGraph={isLineGraph}
                            labelType={labelType}
                            values={xTickValues}
                        />
                        {chartType === 'bar' && (
                            <ColumnChart
                                columnWidth={columnWidth}
                                data={dataPoints}
                                height={containerHeight}
                                onHover={this._toggleTooltip}
                            />
                        )}
                        {isLineGraph && (
                            <LineChart
                                data={dataPoints}
                                onHover={this._toggleTooltip}
                                type={chartType}
                            />
                        )}
                        {showTooltip && (
                            <Tooltip
                                chartType={chartType}
                                containerWidth={containerWidth}
                                data={tooltip}
                                dataType={dataType}
                            />
                        )}
                    </svg>
                </div>
            </div>
        );
    }
}

Chart.propTypes = propTypes;
Chart.defaultProps = defaultProps;

export default Chart;
